"use client";

import { useActionState, useEffect, useState } from "react";

import type { Route } from "next";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { mobileNumberAction } from "@/app/(auth)/actions";

type MobileNumberActionState = {
  success: boolean;
  errors?: string[];
  data?: { data: { otp_id?: string } };
};

export default function LoginForm({
  onChange,
  mobileNumber,
  changeStep,
  steps,
}: {
  onChange: (value: number | null) => void;
  mobileNumber: number | null;
  changeStep: (step: string) => void;
  steps: string[];
}) {
  const router = useRouter();
  const [phoneCountryCode, setPhoneCountryCode] = useState<"+91" | "+971">("+91");
  const [state, formAction, isPending] = useActionState<MobileNumberActionState, FormData>(
    mobileNumberAction,
    { success: false },
  );

  useEffect(() => {
    if (!state.success) return;
    const parsedNumber = Number(mobileNumber);
    onChange(Number.isNaN(parsedNumber) ? null : parsedNumber);
    changeStep(steps[1]);
    const nextRoute =
      `/login/?step=otp-verification&mobileNumber=${mobileNumber}&id=${state.data?.data?.otp_id}` as Route;
    router.replace(nextRoute);
  }, [changeStep, mobileNumber, onChange, router, state, steps]);

  function handleMobileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (value === "") {
      onChange(null);
      return;
    }

    // allow only digits
    if (!/^\d+$/.test(value)) {
      return;
    }

    onChange(Number(value));
  }

  return (
    <div className="min-w-[448px]">
      <Image
        src="/images/logos/app-logo.svg"
        priority
        alt="App Logo"
        width={114}
        height={32}
        placeholder="blur"
        blurDataURL="/images/logos/app-logo.svg"
      />
      <h1 className="text-heading-2xl font-lora text-text-primary mt-[20px]">
        Welcome Back to Jfit
      </h1>

      <form action={formAction} className="mt-[40px] w-full space-y-6">
        <div className="space-y-1">
          <Label htmlFor="contact" className="text-medium-md text-text-primary">
            Enter Phone number
          </Label>
          <div className="flex gap-2">
            <select
              id="phoneCountryCode"
              name="phoneCountryCode"
              value={phoneCountryCode}
              onChange={(e) => setPhoneCountryCode(e.target.value as "+91" | "+971")}
              className="border-input bg-background text-medium-md text-text-primary focus-visible:border-ring focus-visible:ring-ring/50 h-[38px] w-24 rounded-md border px-3 py-2 outline-none focus-visible:ring-[3px]">
              <option value="+91">+91</option>
              <option value="+971">+971</option>
            </select>
            <Input
              value={mobileNumber ?? ""}
              onChange={handleMobileChange}
              id="contact"
              name="mobileNumber"
              type="tel"
              maxLength={15}
              placeholder="Enter phone number"
              className="text-medium-md text-text-primary bg-transparent autofill:bg-transparent focus:bg-transparent"
            />
          </div>
          {state.errors?.length ? (
            <p className="text-destructive text-sm">{state.errors.join(", ")}</p>
          ) : null}
        </div>

        <Button
          type="submit"
          disabled={mobileNumber === null || isPending}
          className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 w-full cursor-pointer rounded-full">
          {isPending ? "Processing..." : "Continue"}
        </Button>
      </form>
    </div>
  );
}
